"use client";

/**
 * ImportFromPapers 组件
 * 
 * 功能概述：
 * 从文献库中选择文献并导入到指定主题的对话框组件。
 * 支持多维度过滤、累加式选择、导入数量限制等功能。
 * 
 * 主要功能：
 * 1. 主题过滤：通过下拉框选择主题，筛选对应主题的文献（通过 API 实现）
 * 2. 文献搜索：支持按文献标题、作者、机构进行实时搜索过滤
 * 3. 导入状态过滤：可选择仅显示未导入过的文献
 * 4. 累加式全选：支持跨多次搜索过滤的累加式选择，选中状态会合并而非覆盖
 * 5. 导入数量限制：检查并限制导入数量，支持 VIP 升级提示
 * 6. 错误处理：显示文献错误状态和页数限制提示
 * 
 * 使用场景：
 * - 在主题文献列表中打开对话框，选择文献导入到当前主题
 * - 在创建文献综述时，选择文献导入到新主题
 * 
 * 核心设计：
 * - rowSelection：保存所有选中的行 ID（跨多次搜索过滤的合并结果）
 * - filterRows：当前视图可见的行（受搜索和导入状态过滤影响）
 * - selectedRows：从所有数据中筛选出的已选中行（用于最终导入）
 * - 全选逻辑：只影响当前过滤结果，选中状态会累加合并
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/fun-ui/Dialog";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useFetchPapers, useSubjects, useUserVip } from "@/stores/appStore";
import { Button } from "@/components/fun-ui/Button";
import { PaperId, type Paper } from "@/stores/slices/paperSlice";
import { Checkbox } from "@/components/ui/checkbox";
import usePathParams, { useSubjectId } from "./hooks/usePathParams";
import toast from "react-hot-toast";
import { useDialog } from "@/hooks/useDialog";
import { UpgradeVipButton } from "../payment/UpgradeVipButton";
import { checkIsExceedMaxPages, ExceedMaxPages } from "@/components/papers/PictureTypePaperMaxPagesTip";
import { Searcher } from "@/components/papers/PaperList/Searcher";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronDownIcon, AlertCircleIcon, LibraryIcon } from "@/components/fun-ui/icons";
import Loading from "@/components/Loading";
import { Tip } from "@/components/fun-ui/Tip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PaperBadge } from "../papers/PaperBadge";

const ALL_SUBJECTS_LABEL = '全部文献';

/**
 * ImportFromPapers 组件暴露的方法接口
 */
export interface ImportFromPapersRef {
  /** 打开弹窗 */
  open: () => void;
}

interface ImportFromPapersProps {
  /** Dialog 打开/关闭状态变化回调 */
  onOpenChange?: (open: boolean) => void;
  /** 导入文献的回调函数，返回是否导入成功 */
  onImport?: (paperIds: PaperId[], papers: Paper[]) => Promise<boolean>;
  /** 触发按钮的自定义样式类名 */
  buttonClassName?: string;
  /** 已经导入的文献 ID 列表（用于判断文献是否已导入） */
  importedPaperIds?: PaperId[];
  /** 是否默认只显示未导入过的文献 */
  showUnImportedOnly?: boolean;
  /** 最大可导入文献数量（用于限制导入数量） */
  maxNumOfPapers?: number;
  /** 已导入的文献数量（用于计算剩余可导入数量） */
  importedNumOfPapers?: number;
}

const ImportFromPapers = forwardRef<ImportFromPapersRef, ImportFromPapersProps>(
  function ImportFromPapers({
    onOpenChange = () => { },
    onImport = async () => true,
    buttonClassName = '',
    importedPaperIds = [],
    showUnImportedOnly = true,
    maxNumOfPapers = 0,
    importedNumOfPapers = -1,
  }, ref) {
    // ========== 状态管理 ==========
    const [open, setOpen] = useState(false);
    const label = '从文献库导入';
    const subjectId = useSubjectId(); // 当前主题 ID（从路由参数获取）

    // 暴露方法给外部使用
    useImperativeHandle(ref, () => ({
      open: () => setOpen(true),
    }), []);
    // 使用本地状态存储当前主题的 papers，避免影响全局缓存
    const [papers, setPapers] = useState<Paper[]>([]);

    // React Table 相关状态
    const [sorting, setSorting] = useState<SortingState>([]); // 排序状态
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]); // 列过滤状态
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({}); // 列可见性状态
    const [rowSelection, setRowSelection] = useState({}); // 行选中状态（key: row.id, value: boolean）

    // 过滤相关状态
    const [selectedSubjectId, setSelectedSubjectId] = useState<string>(''); // 选中的主题 ID（空字符串表示"全部文献"）
    const [searchKeyword, setSearchKeyword] = useState<string>(''); // 文献搜索关键词
    const [subjectSearchQuery, setSubjectSearchQuery] = useState<string>(''); // 主题搜索关键词（用于主题下拉框内搜索）
    const [popoverOpen, setPopoverOpen] = useState(false); // 主题下拉框打开状态
    const [importedOnly, setImportedOnly] = useState(showUnImportedOnly); // 是否仅显示未导入的文献
    const [basePapersLoading, setBasePapersLoading] = useState(false); // base papers 加载状态
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false); // 确认对话框打开状态
    const [pendingSubjectId, setPendingSubjectId] = useState<string | null>(null); // 待切换的主题ID

    // Store hooks
    const { subjects, fetchSubjects, subjectsFetched, fetchSubjectsLoading } = useSubjects();
    const { fetchPapers } = useFetchPapers();

    // ========== Refs ==========
    /** 使用 ref 保存已导入的文献 ID，避免闭包问题 */
    const importedPaperIdsRef = useRef<PaperId[]>(importedPaperIds);
    useEffect(() => {
      importedPaperIdsRef.current = importedPaperIds;
    }, [importedPaperIds]);

    /** 使用 ref 保存当前选中的主题 ID，避免在 callback 中的闭包问题 */
    const selectedSubjectIdRef = useRef<string>(selectedSubjectId);
    useEffect(() => {
      selectedSubjectIdRef.current = selectedSubjectId;
    }, [selectedSubjectId]);

    // ========== 工具函数 ==========
    /**
     * 判断文献是否已导入到指定主题
     * @param paper 文献对象
     * @param subjectId 主题 ID
     * @returns 是否已导入
     */
    const isPaperImported = useCallback((paper: Paper, subjectId: string) => {
      return (
        (paper?.subjects || []).findIndex(({ id }) => id === subjectId) !== -1
        || importedPaperIdsRef.current.includes(paper.paperId.toString())
      );
    }, []);

    /**
     * 判断文献是否有错误（extractStatus < 0 表示提取失败）
     * @param paper 文献对象
     * @returns 是否有错误
     */
    const isPaperError = useCallback((paper: Paper) => {
      return (paper?.extractStatus || 0) < 0
    }, []);

    // ========== 过滤逻辑 ==========
    /**
     * 应用搜索和导入状态过滤
     * 这是基础的过滤逻辑，用于筛选当前视图可见的行
     * 
     * @param rows 待过滤的行数组
     * @returns 过滤后的行数组
     */
    const applySearchAndImportFilter = useCallback((rows: any[]) => {
      let filtered = rows;

      // 搜索过滤：按文献标题、作者、机构进行模糊匹配
      if (searchKeyword.trim()) {
        const keyword = searchKeyword.toLowerCase();
        filtered = filtered.filter((row) => {
          const paper = row.original as Paper;
          return (
            paper.title?.toLowerCase().includes(keyword) ||
            paper.information?.result?.author?.toLowerCase().includes(keyword) ||
            paper.information?.result?.organization?.toLowerCase().includes(keyword)
          );
        });
      }

      // 导入状态过滤：如果开启"仅显示未导入"，则过滤掉已导入的文献
      if (importedOnly) {
        filtered = filtered.filter((row) => !isPaperImported(row.original, subjectId));
      }

      return filtered;
    }, [searchKeyword, importedOnly, subjectId, isPaperImported]);

    /**
     * 获取可选的过滤后的行
     * 在搜索和导入状态过滤的基础上，进一步排除已导入和错误的文献
     * 用于全选 checkbox 的计算，确保只能选中可选的文献
     * 
     * @param tableRows 待过滤的行数组
     * @returns 可选的行数组（已排除已导入和错误的文献）
     */
    const getSelectableFilteredRows = useCallback((tableRows: any[]) => {
      const filtered = applySearchAndImportFilter(tableRows);
      // 只考虑可选的过滤后的行（排除已导入和错误的）
      return filtered.filter(row => {
        const paper = row.original as Paper;
        return !isPaperImported(paper, subjectId) && !isPaperError(paper);
      });
    }, [applySearchAndImportFilter, isPaperImported, isPaperError, subjectId]);

    // ========== 表格列定义 ==========
    /**
     * 定义表格列，包括：
     * - 选择列（全选 checkbox + 行选择 checkbox）
     * - 文献列（显示文献标题、错误状态、页数限制提示）
     * - 信息列（机构、作者、年份、期刊）
     * - 其他列（阅读进度、上传时间）
     */
    const columns: ColumnDef<Paper>[] = useMemo(() => {
      const staticCols: ColumnDef<Paper>[] = [
        {
          id: "select",
          size: 40,
          header: ({ table }) => {
            // 计算当前可选的过滤后的行（用于显示全选状态）
            // 这里获取的是所有数据行，后续会经过 getSelectableFilteredRows 筛选
            const tableRows = table.getCoreRowModel().rows;
            // getSelectableFilteredRows 包含了搜索过滤、导入状态过滤以及排除不可选行（已导入或错误）的逻辑
            const selectableFilteredRows = getSelectableFilteredRows(tableRows);

            // 检查当前可选的过滤后的行是否全部选中
            // 只有当有可选行且所有可选行都被选中时，全选框才显示为选中
            const allFilteredSelected = selectableFilteredRows.length > 0 && selectableFilteredRows.every(row => {
              const rowId = row.id;
              return (rowSelection as Record<string, boolean>)[rowId] === true;
            });

            return (
              <Checkbox
                checked={allFilteredSelected}
                onCheckedChange={(checked) => {
                  // 在回调中重新计算，确保使用最新的数据
                  const currentTableRows = table.getCoreRowModel().rows;
                  const selectableRows = getSelectableFilteredRows(currentTableRows);

                  // 合并选中状态：只将当前可选的过滤后的行的 ID 添加到 rowSelection 中，或从中移除
                  // 这种设计允许用户进行"累加式选择"：搜索 A -> 全选 -> 搜索 B -> 全选 -> 最终包含 A 和 B 的所有结果
                  setRowSelection((prevSelection) => {
                    const newSelection = { ...(prevSelection as Record<string, boolean>) };
                    selectableRows.forEach(row => {
                      const rowId = row.id;
                      if (checked) {
                        // 全选：添加当前可选的过滤后的所有行
                        newSelection[rowId] = true;
                      } else {
                        // 取消全选：移除当前可选的过滤后的所有行
                        // 注意：这不会影响那些不在当前搜索结果中但之前已被选中的行
                        delete newSelection[rowId];
                      }
                    });
                    return newSelection;
                  });
                }}
                aria-label="全选"
                className="translate-y-[2px] data-[state=unchecked]:border-foreground"
              />
            );
          },
          cell: ({ row }) => {
            const paper = row.original as Paper;
            const imported = isPaperImported(paper, subjectId);
            const isError = isPaperError(paper);

            return (
              <Tip
                label={imported ? "该文献已导入" : ""}
                side="right"
                asChild
              >
                <div className="inline-flex translate-y-[2px]">
                  <Checkbox
                    // 已导入或错误的文献显示为选中但禁用状态
                    checked={isError ? false : (row.getIsSelected() || imported)}
                    disabled={imported || isError}
                    onCheckedChange={(value) => {
                      // 已导入或错误的文献不允许切换选中状态
                      if (imported || isError) {
                        return;
                      }
                      row.toggleSelected(!!value);
                    }}
                    aria-label="选中行"
                    className={cn(
                      "data-[state=unchecked]:border-foreground",
                      imported && "data-[state=checked]:bg-annotation-foreground data-[state=checked]:border-annotation-foreground",
                    )}
                  />
                </div>
              </Tip>
            )
          },
          enableSorting: false,
          enableHiding: false,
        },
        {
          accessorKey: "paperName",
          size: 260,
          header: "文献",
          cell: ({ table, row }) => {
            const paper = row.original as Paper;
            const imported = isPaperImported(row.original, subjectId);
            const isError = isPaperError(paper);
            const { extractStatus } = paper;
            return (
              <div className={cn(
                "w-full min-h-[24px]",
                (imported || isError) && "text-annotation-foreground",
              )}
              >
                <h4
                  className="text-sm wrap-break-word cursor-pointer space-x-1"
                  title={paper.paperName ? `${paper.paperName}.pdf` : ''}
                >
                  <PaperBadge className="-mt-[2px]" paper={paper} />
                  {/* 显示文献标题：优先显示 extra.title，否则显示 paperName */}
                  <span>{paper.extra?.title || paper.paperName}</span>
                  {/* 
                  检查文献是否超过最大页数限制
                  如果超过，显示 ExceedMaxPages 组件，提示用户升级会员
                  当用户升级成功后，重新获取 papers 数据并重置选中状态
                */}
                  {
                    checkIsExceedMaxPages(extractStatus) && (
                      <div className="text-destructive text-sm">
                        <ExceedMaxPages
                          extractStatus={extractStatus}
                          callback={async ({ type, status }) => {
                            // 用户升级成功后，重新获取当前主题的 papers 数据（静默模式）
                            // 并重置表格的选中状态
                            if (type === 'upgrade' && status === 'success') {
                              // 使用返回值更新本地状态，保持当前主题过滤
                              // 使用 ref 获取最新的 selectedSubjectId，避免闭包问题
                              // skipGlobalUpdate: true 表示不更新全局缓存，避免影响 ImportPapers 组件
                              const fetchedPapers = await fetchPapers({
                                subjectId: selectedSubjectIdRef.current || '', // 保持当前主题过滤
                                mode: 'base',
                                skipGlobalUpdate: true, // 不更新全局缓存，只返回数据
                              }, true);
                              if (fetchedPapers) {
                                setPapers(fetchedPapers);
                                // 重置选中状态
                                setRowSelection({});
                              }
                            }
                          }}
                        />
                      </div>
                    )
                  }
                  {
                    (isError && !checkIsExceedMaxPages(extractStatus)) ? (
                      <div className="text-destructive text-sm">{paper.error}</div>
                    ) : null
                  }
                </h4>
              </div>
            );
          },
          enableHiding: false,
          enablePinning: true,
        },
      ];
      const informationCols: ColumnDef<Paper>[] = [{
        name: "机构",
        key: "organization",
        size: 320,
      }, {
        name: "作者",
        key: "author",
        size: 320,
      }, {
        name: "年份",
        key: "createAt",
        size: 120,
      }, {
        name: "期刊",
        key: "journal",
        size: 240,
      }].map(
        ({ name, key, size }) => ({
          accessorKey: `information.result[${key}]`,
          size: size!,
          header: name,
          cell: ({ row }) => {
            const paper = row.original as Paper;
            const { result } = paper.information;
            const imported = isPaperImported(row.original, subjectId);
            const isError = isPaperError(paper);
            return (
              <div className={cn(
                "w-full",
                (imported || isError) && "text-annotation-foreground",
              )}>
                <span className="wrap-break-word block">{result[(key as keyof typeof result)] || '未知'}</span>
              </div>
            );
          },
          enableHiding: false,
          enablePinning: true,
        })
      );
      const restCols: ColumnDef<Paper>[] = [
        {
          accessorKey: "currPage",
          size: 160,
          header: "阅读进度",
          cell: ({ row }) => {
            const paper = row.original as Paper;
            const imported = isPaperImported(row.original, subjectId);
            const isError = isPaperError(paper);
            return (
              <div className={cn(
                "w-full",
                (imported || isError) && "text-annotation-foreground",
              )}>
                <span className="wrap-break-word block">{paper.totalPage ? Math.round(paper.currPage / paper.totalPage * 100) : 0}%</span>
              </div>
            );
          },
          enableHiding: false,
          enablePinning: true,
        },
        {
          accessorKey: "createTime",
          size: 240,
          header: "上传时间",
          cell: ({ row, getValue }) => {
            const paper = row.original as Paper;
            const imported = isPaperImported(row.original, subjectId);
            const isError = isPaperError(paper)
            return (
              <div className={cn(
                "w-full",
                (imported || isError) && "text-annotation-foreground",
              )}>
                <span className="wrap-break-word block">{getValue<string>()}</span>
              </div>
            );
          },
          enableHiding: false,
          enablePinning: true,
        },
      ];
      return staticCols.concat(informationCols).concat(restCols);
    }, [isPaperImported, subjectId, isPaperError, fetchPapers, rowSelection, getSelectableFilteredRows, setPapers, setRowSelection, selectedSubjectIdRef]);
    // ========== React Table 配置 ==========
    /**
     * 初始化 React Table 实例
     * - 使用 manualPagination: true，因为数据已通过主题过滤获取
     * - enableRowSelection 控制哪些行可以被选中（排除已导入和错误的）
     */
    const table = useReactTable({
      data: papers || [],
      columns,
      state: {
        sorting,
        columnFilters,
        columnVisibility,
        rowSelection,
      },
      // 只有未导入且无错误的文献才能被选中
      enableRowSelection: (row: any) => !isPaperImported(row.original, subjectId) && !isPaperError(row.original),
      onRowSelectionChange: setRowSelection,
      onSortingChange: setSorting,
      onColumnFiltersChange: setColumnFilters,
      onColumnVisibilityChange: setColumnVisibility,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      manualPagination: true, // 手动分页，因为数据已通过主题过滤
      getSortedRowModel: getSortedRowModel(),
      getFacetedRowModel: getFacetedRowModel(),
      getFacetedUniqueValues: getFacetedUniqueValues(),
    });

    // ========== 数据计算 ==========
    /** 获取表格的所有行（已通过主题过滤，因为 papers 数据来自 fetchPapers） */
    const tableRows = table.getCoreRowModel().rows;

    /**
     * 计算当前视图可见的过滤后的行
     * 注意：主题过滤通过 fetchPapers 的 subjectId 参数在 API 层面实现，这里不需要手动过滤
     * filterRows 仅包含当前视图可见的行（受搜索关键词和"仅显示未导入"开关影响）
     * 用于表格渲染，显示给用户看的行
     */
    const filterRows = useMemo(() => {
      return applySearchAndImportFilter(tableRows);
    }, [tableRows, applySearchAndImportFilter]);

    // 基于过滤后的行计算选中的行
    // rowSelection 保存所有选中的行的 ID（跨多次搜索过滤的合并结果）
    // selectedRows 是从所有 tableRows 中筛选出在 rowSelection 中标记为选中的行
    // 这样设计是为了确保即使在搜索过程中某些已选中的行被过滤掉了，它们依然保持选中状态
    const selectedRows = useMemo(() => {
      // rowSelection 是一个对象，key 是 row.id，value 是 boolean
      // 从所有 tableRows 中筛选出在 rowSelection 中标记为选中的行
      return tableRows.filter(row => {
        const rowId = row.id;
        return (rowSelection as Record<string, boolean>)[rowId] === true;
      });
    }, [tableRows, rowSelection]);

    // ========== 主题相关计算 ==========
    /** 可用的主题列表（排除当前主题，避免重复导入） */
    const availableSubjects = useMemo(() => {
      return subjects.filter(s => s.id !== subjectId);
    }, [subjects, subjectId]);

    /** 根据搜索关键词过滤主题列表（用于主题下拉框内搜索） */
    const filteredSubjects = useMemo(() => {
      if (!subjectSearchQuery.trim()) {
        return availableSubjects;
      }
      return availableSubjects.filter(subject =>
        subject.subjectName.toLowerCase().includes(subjectSearchQuery.toLowerCase())
      );
    }, [availableSubjects, subjectSearchQuery]);

    /** 获取当前选中主题的显示名称 */
    const selectedSubjectName = useMemo(() => {
      if (!selectedSubjectId) {
        return ALL_SUBJECTS_LABEL;
      }
      const subject = subjects.find(s => s.id === selectedSubjectId);
      return subject?.subjectName || ALL_SUBJECTS_LABEL;
    }, [selectedSubjectId, subjects]);

    // ========== 主题切换处理 ==========
    /**
     * 处理主题切换
     * 如果当前有选中的文献且切换的主题与当前不同，先弹出确认对话框
     */
    const handleSubjectChange = useCallback((newSubjectId: string) => {
      // 计算已选中的文献数量
      const selectedCount = Object.keys(rowSelection).filter(
        key => (rowSelection as Record<string, boolean>)[key] === true
      ).length;
      const isDifferentSubject = newSubjectId !== selectedSubjectId;

      // 如果有选中文献且切换的主题不同，显示确认对话框
      if (selectedCount > 0 && isDifferentSubject) {
        setPendingSubjectId(newSubjectId);
        setConfirmDialogOpen(true);
        setPopoverOpen(false);
      } else {
        // 直接切换
        setSelectedSubjectId(newSubjectId);
        setSubjectSearchQuery('');
        setPopoverOpen(false);
      }
    }, [rowSelection, selectedSubjectId]);

    /**
     * 确认切换主题（放弃已选中的文献）
     */
    const handleConfirmSwitch = useCallback(() => {
      if (pendingSubjectId !== null) {
        // 清除选中状态
        table.resetRowSelection();
        // 切换主题
        setSelectedSubjectId(pendingSubjectId);
        setSubjectSearchQuery('');
        setPendingSubjectId(null);
        setConfirmDialogOpen(false);
      }
    }, [pendingSubjectId, table]);

    /**
     * 取消切换主题
     */
    const handleCancelSwitch = useCallback(() => {
      setPendingSubjectId(null);
      setConfirmDialogOpen(false);
    }, []);

    // ========== 副作用处理 ==========
    /**
     * Dialog 打开时重置所有状态
     * 确保每次打开对话框都是干净的状态
     */
    useEffect(() => {
      if (open) {
        // 重置过滤状态
        setSelectedSubjectId('');
        setSearchKeyword('');
        setSubjectSearchQuery('');
        table.resetRowSelection();
        setConfirmDialogOpen(false);
        setPendingSubjectId(null);
      }
    }, [open, table]);

    /**
     * 当选择主题改变时，重新获取对应主题的文献列表
     * 包括 Dialog 打开时的初始加载（selectedSubjectId 为空字符串表示"全部文献"）
     * 
     * 注意：主题过滤在 API 层面实现，通过 fetchPapers 的 subjectId 参数
     * 使用返回值更新本地状态，避免影响全局缓存
     */
    useEffect(() => {
      let isMounted = true;
      async function fetchPapersBySubject() {
        if (open) {
          setBasePapersLoading(true);
          try {
            // 使用返回值更新本地状态，而不是依赖全局缓存
            // skipGlobalUpdate: true 表示不更新全局缓存，避免影响 ImportPapers 组件
            const fetchedPapers = await fetchPapers({
              subjectId: selectedSubjectId || '', // 空字符串表示获取全部文献的文献
              mode: 'base',
              skipGlobalUpdate: true, // 不更新全局缓存，只返回数据
            }, true); // true 表示静默模式，不显示加载提示
            // 注意：切换主题后不在这里重置选中状态，由 handleConfirmSwitch 处理
            if (isMounted && fetchedPapers) {
              setPapers(fetchedPapers);
            }
          } finally {
            if (isMounted) {
              setBasePapersLoading(false);
            }
          }
        }
      }
      fetchPapersBySubject();
      return () => {
        isMounted = false; // 组件卸载时标记，避免在已卸载的组件上设置状态
      };
    }, [selectedSubjectId, fetchPapers, open]);

    /**
     * 数据获取优化：在 Dialog 打开时，如果主题列表未获取，自动调用 fetchSubjects()
     * 确保主题下拉框有数据可用
     */
    useEffect(() => {
      if (open && !subjectsFetched && subjects.length === 0) {
        fetchSubjects();
      }
    }, [open, subjectsFetched, subjects.length, fetchSubjects]);

    // ========== 其他 Hooks ==========
    const { vip } = useUserVip(); // 获取用户 VIP 状态，用于导入数量限制判断
    const { alert } = useDialog(); // 获取对话框工具，用于显示升级提示

    return (
      <>
        {/* 切换主题确认对话框 */}
        <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <AlertCircleIcon className="h-5 w-5 text-orange-500" />
                <AlertDialogTitle>
                  当前已选中 {selectedRows.length} 篇文献
                </AlertDialogTitle>
              </div>
            </AlertDialogHeader>
            <AlertDialogDescription className="text-left">
              <span className="block mb-1">切换分类将导致勾选失效。</span>
              <span className="block">建议先导入这 {selectedRows.length} 篇文献，再进行切换。</span>
            </AlertDialogDescription>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={handleCancelSwitch}>
                暂不切换
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmSwitch}>
                放弃并切换
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Dialog
          open={open}
          onOpenChange={(o) => {
            onOpenChange(o);
            setOpen(o);
          }}
        >
          <DialogTrigger asChild>
            <div>
              <Tip
                label={label}
                side="top"
                asChild
              >
                <Button
                  className={
                    cn(
                      "cursor-pointer text-primary border-primary hover:bg-primary/5 hover:text-primary min-w-[40px] xl:min-w-[130px] select-none whitespace-nowrap",
                      buttonClassName
                    )
                  }
                  variant="outline"
                >
                  <LibraryIcon size={20} />
                  <span className="hidden xl:inline">{label}</span>
                </Button>
              </Tip>
            </div>
          </DialogTrigger>
          <DialogContent className="h-[90vh] w-full max-w-[calc(100vw-2rem)] lg:max-w-[1200px] space-y-3 p-10 flex flex-col">
            <DialogDescription className="sr-only">

            </DialogDescription>
            <DialogHeader>
              <DialogTitle className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <span className="text-2xl whitespace-nowrap">{label}</span>
                  {/* 
                主题过滤下拉框
                功能：选择要查看的主题，通过 API 获取对应主题的文献列表
                设计：使用 Popover 实现下拉选择，支持主题搜索
              */}
                  <Popover open={popoverOpen} onOpenChange={(open) => {
                    setPopoverOpen(open);
                    // 关闭 Popover 时重置主题搜索框内容
                    if (!open) {
                      setSubjectSearchQuery('');
                    }
                  }}>
                    <PopoverTrigger asChild>
                      <button
                        className={cn(
                          "flex h-10 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm placeholder:text-annotation-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50",
                          "w-[280px]"
                        )}
                      >
                        <span className="text-sm font-normal line-clamp-1 flex-1 min-w-0 text-left mr-2 text-annotation-foreground">
                          {selectedSubjectName}
                        </span>
                        <ChevronDownIcon className="h-4 w-4 opacity-50 shrink-0" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-[280px] p-0 !z-[200] max-w-[280px]"
                      align="start"
                      onOpenAutoFocus={(e) => e.preventDefault()}
                      onWheel={(e) => {
                        // 允许 Popover 内部滚动，防止事件传播到外层 Dialog
                        e.stopPropagation();
                      }}
                    >
                      <div className="flex flex-col">
                        {/* 搜索框 */}
                        <div className="px-5 pt-2 pb-2 border-b">
                          <Searcher
                            defaultValue={subjectSearchQuery}
                            placeholder="搜索主题"
                            debounceTime={0}
                            onSearch={async (searchString: string) => {
                              setSubjectSearchQuery(searchString);
                            }}
                            className="pl-6 pr-3 h-9 text-sm text-annotation-foreground border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent"
                            iconSize={16}
                            iconClassName="left-0"
                          />
                        </div>

                        {/* 
                      全部文献选项 - 固定在顶部，不参与滚动
                      点击后选择"全部文献"（selectedSubjectId 为空字符串），会获取所有主题的文献
                    */}
                        <div className="pt-2">
                          <div
                            onClick={() => {
                              handleSubjectChange(''); // 空字符串表示"全部文献"
                            }}
                            className={cn(
                              "px-3 py-2 mx-2 my-1 rounded-[4px] cursor-pointer transition-colors hover:bg-accent",
                              selectedSubjectId === '' ? "bg-accent" : "hover:bg-accent"
                            )}
                          >
                            <span className={cn(
                              "text-sm block line-clamp-1 break-words",
                            )}>
                              {ALL_SUBJECTS_LABEL}
                            </span>
                          </div>
                        </div>

                        {/* 
                      主题列表 - 可滚动区域
                      显示所有可用主题（排除当前主题），支持搜索过滤
                      最大高度 300px，超出时显示滚动条
                    */}
                        <div className="max-h-[300px] overflow-y-auto overflow-x-hidden pb-2">
                          {fetchSubjectsLoading ? (
                            <div className="flex items-center justify-center py-8">
                              <Loading size="sm" />
                            </div>
                          ) : filteredSubjects.length > 0 ? (
                            <div>
                              {filteredSubjects.map((subject) => (
                                <div
                                  key={subject.id}
                                  onClick={() => {
                                    handleSubjectChange(subject.id);
                                  }}
                                  className={cn(
                                    "px-3 py-2 mx-2 my-1 rounded-[4px] cursor-pointer transition-colors",
                                    selectedSubjectId === subject.id ? "bg-accent" : "hover:bg-accent"
                                  )}
                                  title={subject.subjectName}
                                >
                                  <span className="text-sm block line-clamp-1 break-words h-5">
                                    {subject.subjectName}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="px-5 py-4 text-sm text-muted-foreground text-center">
                              {subjectSearchQuery ? '未找到匹配的主题' : '暂无主题'}
                            </div>
                          )}
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex items-center gap-4">
                  {/* 
                文献搜索框
                功能：实时搜索文献，支持按标题、作者、机构过滤
                使用 Searcher 组件，支持防抖和拼音输入处理
              */}
                  <Searcher
                    defaultValue={searchKeyword}
                    placeholder="搜索文献标题、作者、机构"
                    onSearch={async (searchString: string) => {
                      setSearchKeyword(searchString);
                    }}
                  />
                  {/* 
                仅显示未导入过的文献复选框
                功能：切换是否只显示未导入的文献，用于快速筛选
              */}
                  <label className="flex items-center justify-center space-x-2 text-sm font-normal">
                    <Checkbox
                      checked={importedOnly}
                      onCheckedChange={(value) => setImportedOnly(!!value)}
                      aria-label="仅显示未导入过的文献"
                      className="data-[state=unchecked]:border-foreground"
                    />
                    <span>仅显示未导入过的文献</span>
                  </label>
                </div>
              </DialogTitle>
            </DialogHeader>
            <div className="flex flex-1 overflow-hidden">
              <div className="w-full rounded-lg border max-h-full flex overflow-auto relative">
                {/* 
              basePapers 加载时的遮罩
              当切换主题或 Dialog 打开时，显示加载状态，防止用户在数据加载过程中操作
            */}
                {basePapersLoading && (
                  <Loading size="sm" mask zIndex={50} />
                )}
                <Table className="rounded-l-lg table-fixed relative bg-background" style={{ minWidth: '1700px', tableLayout: 'fixed' }}>
                  <TableHeader className={cn(
                    "sticky top-0 z-20 border-0",
                    "after:absolute after:inset-x-0 after:bottom-0 after:border-b after:border-border",
                  )}>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow
                        key={headerGroup.id}
                        className="bg-muted/95 text-foreground hover:bg-muted"
                      >
                        {headerGroup.headers.map((header, i) => {
                          return (
                            <TableHead
                              key={header.id}
                              className={cn(
                                "text-foreground",
                                i === 0 && "sticky left-0 z-30 bg-muted border-0 pl-5",
                                i === 1 && "sticky left-10 z-30 bg-muted border-0",
                                i === 1 && headerGroup.headers.length > 2 && "after:absolute after:inset-y-0 after:right-0 after:border-r after:border-border",
                              )}
                              style={{ width: `${header.getSize()}px` }}
                            >
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                            </TableHead>
                          )
                        })}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {filterRows.length ? (
                      filterRows.map((row, index) => {
                        const paper = row.original as Paper;
                        const imported = isPaperImported(row.original, subjectId);
                        const isError = isPaperError(paper);
                        return (
                          (
                            <TableRow
                              key={row.id}
                              // data-state={row.getIsSelected() && "selected"}
                              className={cn(
                                "relative group/row",
                                "bg-background hover:bg-accent-lighten",
                                "cursor-pointer"
                              )}
                              onClick={() => {
                                if (!imported && !isError) {
                                  row.toggleSelected(!row.getIsSelected());
                                }
                              }}
                            >
                              {
                                row.getVisibleCells().map((cell: any, i: number) => {
                                  return (
                                    <TableCell
                                      key={cell.id}
                                      data-row={row.id}
                                      // data-state={row.getIsSelected() && "selected"}
                                      className={cn(
                                        "relative p-5 group-hover/row:bg-accent-lighten",
                                        i === 0 && "sticky left-0 z-10 bg-background border-0",
                                        i === 1 && "sticky left-10 z-10 bg-background border-0",
                                        i === 1 && row.getVisibleCells().length > 2 && "after:absolute after:inset-y-0 after:right-0 after:border-r after:border-border",
                                      )}
                                      style={{ width: `${cell.column.getSize()}px` }}
                                    >
                                      {
                                        flexRender(
                                          cell.column.columnDef.cell,
                                          cell.getContext(),
                                        )
                                      }
                                    </TableCell>
                                  );
                                })
                              }
                            </TableRow>
                          )
                        )
                      })
                    ) : (
                      /* 
                        空状态提示
                        - 如果 papers.length === 0：表示当前主题下没有任何文献，显示"无未导入过的文献"
                        - 如果 papers.length > 0：表示有文献但被过滤掉了，显示"未找到符合条件的文献"
                      */
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          className="w-full h-full text-center align-middle"
                        >
                          {papers.length === 0 ? '无未导入过的文献' : '未找到符合条件的文献'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
            <DialogFooter>
              <div className="flex items-center justify-between w-full">
                <div className="text-sm w-fit">
                  已选择 {selectedRows.length} 篇文献
                </div>
                <div className="flex space-x-4">
                  <Button variant="outline" onClick={() => {
                    onOpenChange(false);
                    setOpen(false);
                  }}>取消</Button>
                  <Button
                    className="min-w-[130px]"
                    disabled={!selectedRows.length}
                    onClick={async () => {
                      const papers = selectedRows.map((row) => {
                        const paper = row.original as Paper;
                        return paper;
                      });
                      const paperIds = papers.map((paper) => {
                        return paper.paperId;
                      });
                      // ========== 导入数量限制检查 ==========
                      // 如果设置了最大导入数量限制，需要检查是否超过限制
                      if (maxNumOfPapers && importedNumOfPapers >= 0) {
                        // 计算剩余可导入数量
                        const remaining = maxNumOfPapers - importedNumOfPapers;
                        // 检查已选中的数量是否超过剩余可导入数量
                        if (papers.length - remaining > 0) {
                          if (vip === 0) {
                            // 非 VIP 用户：显示升级提示对话框
                            await alert({
                              title: '升级 Plus 会员可支持 AI Max「Deep」 模式',
                              content: `当前可导入最多 ${maxNumOfPapers} 篇文献，升级 Plus 会员后使用 AI Max「Deep」 模式最多可导入 100 篇文献。`,
                              cancelText: '重选',
                              ConfirmButton: <UpgradeVipButton />,
                            });
                          } else {
                            // VIP 用户：显示提示信息，要求重选
                            toast(`累计超过 ${maxNumOfPapers} 篇（本次可选 ${remaining} 篇），请重选！`, {
                              icon: '⚠️',
                              style: {
                                maxWidth: 'fit-content',
                              },
                            });
                          }
                          return; // 超过限制，不执行导入
                        }
                      }

                      // ========== 执行导入 ==========
                      const success = await onImport(paperIds, papers);
                      if (success) {
                        // 导入成功后关闭对话框
                        onOpenChange(false);
                        setOpen(false);
                        // 注意：成功提示由父组件处理，这里不显示 toast
                      }
                    }}>导入</Button>
                </div>
              </div>

            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }
);

export default ImportFromPapers;

